import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Download,
  ArrowRight,
  Check,
  X,
  Building2,
  User,
  Loader2,
  RefreshCw,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import debounce from 'lodash.debounce';

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500'
};

const statusLabels = {
  draft: 'Brouillon',
  sent: 'Envoyé',
  accepted: 'Accepté',
  rejected: 'Refusé',
  paid: 'Payé',
  cancelled: 'Annulé'
};

const DEFAULT_PRICE_MODE = 'HT';

const calculateLineTotals = (item) => {
  const quantity = Number(item.quantity || 0);
  const unitPrice = Number(item.unit_price || 0);
  const tvaRate = Number(item.tva_rate || 0);
  const discountPercent = Number(item.discount_percent || 0);
  const discountMultiplier = Math.max(0, 1 - discountPercent / 100);
  const normalizedMode = String(item.price_mode || DEFAULT_PRICE_MODE).toUpperCase() === 'TTC' ? 'TTC' : 'HT';

  if (normalizedMode === 'TTC') {
    const lineTtc = quantity * unitPrice * discountMultiplier;
    const divider = 1 + tvaRate / 100;
    const lineHt = divider > 0 ? lineTtc / divider : lineTtc;
    const lineTva = lineTtc - lineHt;
    return { lineHt, lineTva, lineTtc };
  }

  const lineHt = quantity * unitPrice * discountMultiplier;
  const lineTva = lineHt * (tvaRate / 100);
  const lineTtc = lineHt + lineTva;
  return { lineHt, lineTva, lineTtc };
};

export const InvoicingPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [companySearchOpen, setCompanySearchOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewType, setPreviewType] = useState('quote');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Form states
  const [documentType, setDocumentType] = useState('quote');
  const [selectedClient, setSelectedClient] = useState(null);
  const [documentPriceMode, setDocumentPriceMode] = useState(DEFAULT_PRICE_MODE);
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0, price_mode: DEFAULT_PRICE_MODE, tva_rate: 20, discount_percent: 0 }]);
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [dueDays, setDueDays] = useState(30);
  const [editingDocId, setEditingDocId] = useState(null);
  
  // Client form
  const [clientForm, setClientForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', postal_code: '', siret: '', tva_number: '', is_company: false
  });
  
  // Company search
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [searchingCompany, setSearchingCompany] = useState(false);
  
  // Client name suggestions
  const [allClientNames, setAllClientNames] = useState([]);
  const [clientNameSuggestions, setClientNameSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, invoicesRes, clientsRes, namesRes] = await Promise.all([
        api.get('/quotes'),
        api.get('/invoices'),
        api.get('/clients'),
        api.get('/clients/names')
      ]);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setAllClientNames(namesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Company search with debounce
  const searchCompany = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setCompanyResults([]);
        return;
      }
      setSearchingCompany(true);
      try {
        const response = await api.get(`/search-company?q=${encodeURIComponent(query)}`);
        setCompanyResults(response.data);
      } catch (error) {
        console.error('Company search error:', error);
      } finally {
        setSearchingCompany(false);
      }
    }, 300),
    [api]
  );

  useEffect(() => {
    searchCompany(companyQuery);
  }, [companyQuery, searchCompany]);

  const selectCompany = (company) => {
    setClientForm({
      ...clientForm,
      name: company.name,
      address: company.address,
      city: company.city,
      postal_code: company.postal_code,
      siret: company.siret,
      tva_number: company.tva_number,
      is_company: true
    });
    setCompanySearchOpen(false);
    setCompanyQuery('');
    setCompanyResults([]);
  };

  // Document handlers
  const openNewDocument = (type) => {
    setDocumentType(type);
    setEditingDocId(null);
    setSelectedClient(null);
    setDocumentPriceMode(DEFAULT_PRICE_MODE);
    setItems([{ description: '', quantity: 1, unit_price: 0, price_mode: DEFAULT_PRICE_MODE, tva_rate: 20, discount_percent: 0 }]);
    setNotes('');
    setPaymentTerms('');
    setValidityDays(30);
    setDueDays(30);
    setDocumentDialogOpen(true);
  };

  const openEditDocument = (type, doc) => {
    setDocumentType(type);
    setEditingDocId(doc.id);
    const client = clients.find((c) => c.id === doc.client_id) || (doc.client ? doc.client : null);
    setSelectedClient(client || (doc.client_id ? { id: doc.client_id, name: doc.client_name || '' } : null));
    const docItems = (doc.items || []).map((it) => ({
      description: it.description || '',
      quantity: it.quantity != null ? it.quantity : 1,
      unit_price: it.unit_price != null ? it.unit_price : 0,
      price_mode: (String(it.price_mode || DEFAULT_PRICE_MODE).toUpperCase() === 'TTC') ? 'TTC' : 'HT',
      tva_rate: it.tva_rate != null ? it.tva_rate : 20,
      discount_percent: it.discount_percent != null ? it.discount_percent : 0,
    }));
    setItems(docItems.length > 0 ? docItems : [{ description: '', quantity: 1, unit_price: 0, price_mode: DEFAULT_PRICE_MODE, tva_rate: 20, discount_percent: 0 }]);
    setDocumentPriceMode(docItems[0]?.price_mode || DEFAULT_PRICE_MODE);
    setNotes(doc.notes || '');
    setPaymentTerms(doc.payment_terms || '');
    setValidityDays(doc.validity_days != null ? doc.validity_days : 30);
    setDueDays(doc.due_days != null ? doc.due_days : 30);
    setDocumentDialogOpen(true);
  };

  const addItem = () => {
    setItems([
      ...items,
      { description: '', quantity: 1, unit_price: 0, price_mode: documentPriceMode, tva_rate: 20, discount_percent: 0 }
    ]);
  };

  const applyGlobalPriceModeToAllItems = () => {
    setItems((prevItems) => prevItems.map((item) => ({ ...item, price_mode: documentPriceMode })));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTva = 0;
    items.forEach(item => {
      const { lineHt, lineTva } = calculateLineTotals(item);
      subtotal += lineHt;
      totalTva += lineTva;
    });
    return { subtotal: subtotal.toFixed(2), tva: totalTva.toFixed(2), total: (subtotal + totalTva).toFixed(2) };
  };

  const handleCreateDocument = async () => {
    if (!selectedClient) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Veuillez remplir tous les articles');
      return;
    }
    if (items.some(item => Number(item.tva_rate) < 0)) {
      toast.error('Le taux de TVA ne peut pas être négatif');
      return;
    }

    try {
      const payload = {
        client_id: selectedClient.id,
        items: items.map(item => ({
          description: item.description,
          quantity: Number.isFinite(parseFloat(item.quantity)) ? parseFloat(item.quantity) : 1,
          unit_price: Number.isFinite(parseFloat(item.unit_price)) ? parseFloat(item.unit_price) : 0,
          price_mode: String(item.price_mode || DEFAULT_PRICE_MODE).toUpperCase() === 'TTC' ? 'TTC' : 'HT',
          tva_rate: Number.isFinite(parseFloat(item.tva_rate)) ? parseFloat(item.tva_rate) : 0,
          discount_percent: parseFloat(item.discount_percent) || 0
        })),
        notes,
        payment_terms: paymentTerms
      };

      let createdDoc;
      if (documentType === 'quote') {
        payload.validity_days = validityDays;
        if (editingDocId) {
          const response = await api.put(`/quotes/${editingDocId}`, payload);
          createdDoc = response.data;
          toast.success('Devis modifié avec succès');
        } else {
          const response = await api.post('/quotes', payload);
          createdDoc = response.data;
          toast.success('Devis créé avec succès');
        }
      } else {
        payload.due_days = dueDays;
        if (editingDocId) {
          const response = await api.put(`/invoices/${editingDocId}`, payload);
          createdDoc = response.data;
          toast.success('Facture modifiée avec succès');
        } else {
          const response = await api.post('/invoices', payload);
          createdDoc = response.data;
          toast.success('Facture créée avec succès');
        }
      }

      setDocumentDialogOpen(false);
      setEditingDocId(null);
      fetchData();
      
      // Show preview
      if (createdDoc) {
        handlePreviewDocument(documentType, createdDoc.id, createdDoc.number);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handlePreviewDocument = async (type, id, number) => {
    setPreviewLoading(true);
    setPreviewType(type);
    setPreviewDocument({ id, number });
    setPreviewDialogOpen(true);
    
    try {
      const response = await api.get(`/${type}s/${id}/preview`);
      setPreviewHtml(response.data.html);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'aperçu');
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeleteDocument = async (type, id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    try {
      await api.delete(`/${type}s/${id}`);
      toast.success('Document supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/convert-to-invoice`);
      toast.success('Devis converti en facture');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/mark-paid`);
      toast.success('Facture marquée comme payée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDownloadPdf = async (type, id, number) => {
    try {
      const response = await api.get(`/${type}s/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const handleUpdateStatus = async (type, id, status) => {
    try {
      await api.put(`/${type}s/${id}`, { status });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Client handlers
  const handleSaveClient = async (e) => {
    e.preventDefault();
    if (!clientForm.name) {
      toast.error('Le nom est requis');
      return;
    }
    try {
      if (editingClient) {
        const response = await api.put(`/clients/${editingClient.id}`, clientForm);
        toast.success('Client modifié');
        const updatedClient = response.data;
        setClients((prev) => prev.map((client) => (client.id === updatedClient.id ? updatedClient : client)));
        if (selectedClient?.id === updatedClient.id) {
          setSelectedClient(updatedClient);
        }
      } else {
        const response = await api.post('/clients', clientForm);
        toast.success('Client créé');
        setClients((prev) => [...prev, response.data]);
        setSelectedClient(response.data);
      }
      setClientDialogOpen(false);
      resetClientForm();
      setEditingClient(null);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la sauvegarde du client');
    }
  };

  const resetClientForm = () => {
    setClientForm({
      name: '', email: '', phone: '', address: '', city: '', postal_code: '', siret: '', tva_number: '', is_company: false
    });
  };

  const openNewClientDialog = () => {
    setEditingClient(null);
    resetClientForm();
    setClientDialogOpen(true);
  };

  const openEditClientDialog = (client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      siret: client.siret || '',
      tva_number: client.tva_number || '',
      is_company: !!client.is_company
    });
    setClientDialogOpen(true);
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Supprimer le client "${client.name}" ? Les devis/factures existants seront conservés.`)) {
      return;
    }

    try {
      await api.delete(`/clients/${client.id}`);
      toast.success('Client supprimé');
      setClients((prev) => prev.filter((item) => item.id !== client.id));
      if (selectedClient?.id === client.id) {
        setSelectedClient(null);
      }
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression du client');
    }
  };

  // Client name autocomplete
  const filterNameSuggestions = (value) => {
    if (!value || value.length < 2) {
      setClientNameSuggestions([]);
      return;
    }
    const filtered = allClientNames.filter(name => 
      name.toLowerCase().includes(value.toLowerCase())
    );
    setClientNameSuggestions(filtered.slice(0, 10));
  };

  const handleClientNameInput = (value) => {
    setClientForm({ ...clientForm, name: value });
    filterNameSuggestions(value);
    setShowNameSuggestions(true);
  };

  const selectNameSuggestion = (name) => {
    setClientForm({ ...clientForm, name: name });
    setShowNameSuggestions(false);
    setClientNameSuggestions([]);
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#065F46]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoicing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900">Devis & Factures</h1>
          <p className="text-slate-500">Gérez vos documents commerciaux</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => openNewDocument('quote')}
            variant="outline"
            className="border-[#065F46] text-[#065F46] hover:bg-[#065F46]/5"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Devis
          </Button>
          <Button 
            onClick={() => openNewDocument('invoice')}
            className="bg-[#065F46] hover:bg-[#047857] text-white"
            data-testid="new-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Facture
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('quotes')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'quotes' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="quotes-tab"
        >
          Devis ({quotes.length})
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'invoices' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="invoices-tab"
        >
          Factures ({invoices.length})
        </button>
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'clients' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="clients-tab"
        >
          Clients ({clients.length})
        </button>
      </div>

      {/* Quotes Tab */}
      {activeTab === 'quotes' && (
        <div className="space-y-4">
          {quotes.length === 0 ? (
            <Card className="border border-slate-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Aucun devis pour le moment</p>
                <Button onClick={() => openNewDocument('quote')} className="bg-[#065F46] hover:bg-[#047857] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Créer un devis
                </Button>
              </CardContent>
            </Card>
          ) : (
            quotes.map((quote, index) => (
              <Card key={quote.id} className="border border-slate-200 hover:shadow-md transition-shadow" data-testid={`quote-${index}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-slate-900">{quote.number}</span>
                          <Badge className={statusColors[quote.status]}>{statusLabels[quote.status]}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">{quote.client_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#065F46]">{quote.total_ttc?.toFixed(2)} €</p>
                        <p className="text-xs text-slate-500">
                          {quote.created_at && format(parseISO(quote.created_at), 'dd/MM/yyyy', { locale: fr })}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`quote-menu-${index}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePreviewDocument('quote', quote.id, quote.number)}>
                            <Eye className="w-4 h-4 mr-2" /> Aperçu
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDocument('quote', quote)} data-testid={`quote-edit-${index}`}>
                            <Edit className="w-4 h-4 mr-2" /> Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPdf('quote', quote.id, quote.number)}>
                            <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                          </DropdownMenuItem>
                          {quote.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleUpdateStatus('quote', quote.id, 'sent')}>
                              <ArrowRight className="w-4 h-4 mr-2" /> Marquer envoyé
                            </DropdownMenuItem>
                          )}
                          {(quote.status === 'draft' || quote.status === 'sent') && !quote.converted_to_invoice_id && (
                            <DropdownMenuItem onClick={() => handleConvertToInvoice(quote.id)}>
                              <Check className="w-4 h-4 mr-2" /> Convertir en facture
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteDocument('quote', quote.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          {invoices.length === 0 ? (
            <Card className="border border-slate-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Aucune facture pour le moment</p>
                <Button onClick={() => openNewDocument('invoice')} className="bg-[#065F46] hover:bg-[#047857] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Créer une facture
                </Button>
              </CardContent>
            </Card>
          ) : (
            invoices.map((invoice, index) => (
              <Card key={invoice.id} className="border border-slate-200 hover:shadow-md transition-shadow" data-testid={`invoice-${index}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        invoice.status === 'paid' ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                        <FileText className={`w-5 h-5 ${invoice.status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-slate-900">{invoice.number}</span>
                          <Badge className={statusColors[invoice.status]}>{statusLabels[invoice.status]}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">{invoice.client_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#065F46]">{invoice.total_ttc?.toFixed(2)} €</p>
                        <p className="text-xs text-slate-500">
                          Échéance: {invoice.due_date && format(parseISO(invoice.due_date), 'dd/MM/yyyy', { locale: fr })}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`invoice-menu-${index}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePreviewDocument('invoice', invoice.id, invoice.number)}>
                            <Eye className="w-4 h-4 mr-2" /> Aperçu
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDocument('invoice', invoice)} data-testid={`invoice-edit-${index}`}>
                            <Edit className="w-4 h-4 mr-2" /> Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPdf('invoice', invoice.id, invoice.number)}>
                            <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                          </DropdownMenuItem>
                          {invoice.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => handleMarkPaid(invoice.id)}>
                              <Check className="w-4 h-4 mr-2" /> Marquer payée
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteDocument('invoice', invoice.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={openNewClientDialog}
              className="bg-[#065F46] hover:bg-[#047857] text-white"
              data-testid="new-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Nouveau Client
            </Button>
          </div>
          {clients.length === 0 ? (
            <Card className="border border-slate-200">
              <CardContent className="py-12 text-center">
                <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Aucun client pour le moment</p>
                <Button onClick={openNewClientDialog} className="bg-[#065F46] hover:bg-[#047857] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un client
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client, index) => (
                <Card key={client.id} className="border border-slate-200 hover:shadow-md transition-shadow" data-testid={`client-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        client.is_company ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        {client.is_company ? (
                          <Building2 className="w-5 h-5 text-blue-600" />
                        ) : (
                          <User className="w-5 h-5 text-slate-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{client.name}</p>
                        {client.email && <p className="text-sm text-slate-500 truncate">{client.email}</p>}
                        {client.siret && <p className="text-xs text-slate-400">SIRET: {client.siret}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditClientDialog(client)}
                          data-testid={`edit-client-btn-${index}`}
                        >
                          <Edit className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClient(client)}
                          data-testid={`delete-client-btn-${index}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New/Edit Document Dialog */}
      <Dialog open={documentDialogOpen} onOpenChange={(open) => { setDocumentDialogOpen(open); if (!open) setEditingDocId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingDocId
                ? (documentType === 'quote' ? 'Modifier le devis' : 'Modifier la facture')
                : (documentType === 'quote' ? 'Nouveau Devis' : 'Nouvelle Facture')}
            </DialogTitle>
            <DialogDescription>
              {editingDocId
                ? `Mettez à jour les informations de ${documentType === 'quote' ? 'ce devis' : 'cette facture'}.`
                : `Créez un ${documentType === 'quote' ? 'devis' : 'facture'} pour votre client.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Client Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Client *</Label>
                <Button 
                  type="button" 
                  variant="link" 
                  size="sm" 
                  onClick={openNewClientDialog}
                  className="text-[#065F46] h-auto p-0"
                >
                  + Nouveau client
                </Button>
              </div>
              <Select 
                value={selectedClient?.id || ''} 
                onValueChange={(id) => setSelectedClient(clients.find(c => c.id === id))}
              >
                <SelectTrigger data-testid="document-client-select">
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} {client.siret && `(${client.siret})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Articles</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={documentPriceMode}
                    onValueChange={(mode) => setDocumentPriceMode(mode)}
                  >
                    <SelectTrigger className="h-9 w-[130px]" data-testid="document-price-mode-select">
                      <SelectValue placeholder="Mode prix" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HT">Mode HT</SelectItem>
                      <SelectItem value="TTC">Mode TTC</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9"
                    onClick={applyGlobalPriceModeToAllItems}
                    data-testid="apply-price-mode-all-button"
                  >
                    Appliquer à toutes les lignes
                  </Button>
                </div>
              </div>
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg">
                  <div className="col-span-12 md:col-span-4">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder="Description"
                      className="h-9"
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Label className="text-xs">Qté</Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                      className="h-9"
                      data-testid={`item-quantity-${index}`}
                    />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-xs">Mode prix</Label>
                    <Select
                      value={String(item.price_mode || DEFAULT_PRICE_MODE).toUpperCase() === 'TTC' ? 'TTC' : 'HT'}
                      onValueChange={(mode) => updateItem(index, 'price_mode', mode)}
                    >
                      <SelectTrigger className="h-9" data-testid={`item-price-mode-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HT">HT</SelectItem>
                        <SelectItem value="TTC">TTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-xs">Prix saisi</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="h-9 font-mono"
                      data-testid={`item-price-${index}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <Label className="text-xs">TVA %</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.tva_rate}
                      onChange={(e) => updateItem(index, 'tva_rate', parseFloat(e.target.value) || 0)}
                      className="h-9 font-mono"
                      data-testid={`item-tva-rate-${index}`}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1 flex items-end justify-end">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      className="h-9 w-9 text-slate-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Ajouter un article
              </Button>
            </div>

            {/* Totals */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Total HT</span>
                <span className="font-mono">{totals.subtotal} €</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">TVA</span>
                <span className="font-mono">{totals.tva} €</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total TTC</span>
                <span className="font-mono text-[#065F46]">{totals.total} €</span>
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-4">
              {documentType === 'quote' ? (
                <div className="space-y-2">
                  <Label>Validité (jours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={validityDays}
                    onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
                    data-testid="validity-days-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Échéance (jours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={dueDays}
                    onChange={(e) => setDueDays(parseInt(e.target.value) || 30)}
                    data-testid="due-days-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Conditions de paiement</Label>
                <Input
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder="Paiement à 30 jours"
                  data-testid="payment-terms-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes ou commentaires..."
                rows={3}
                data-testid="document-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={handleCreateDocument}
              className="bg-[#065F46] hover:bg-[#047857] text-white"
              data-testid="create-document-btn"
            >
              {editingDocId
                ? `Enregistrer ${documentType === 'quote' ? 'le devis' : 'la facture'}`
                : `Créer ${documentType === 'quote' ? 'le devis' : 'la facture'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Client Dialog */}
      <Dialog
        open={clientDialogOpen}
        onOpenChange={(open) => {
          setClientDialogOpen(open);
          if (!open) {
            setEditingClient(null);
            resetClientForm();
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit" data-testid="client-dialog-title">
              {editingClient ? 'Modifier le client' : 'Nouveau Client'}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? 'Modifiez les informations du client.' : 'Ajoutez les informations du client.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveClient} className="space-y-4">
            {/* Company Search */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Button 
                  type="button" 
                  variant={companySearchOpen ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCompanySearchOpen(!companySearchOpen)}
                  className={companySearchOpen ? 'bg-[#065F46] hover:bg-[#047857]' : ''}
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  Rechercher une entreprise
                </Button>
              </div>
              
              {companySearchOpen && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={companyQuery}
                      onChange={(e) => setCompanyQuery(e.target.value)}
                      placeholder="Nom de l'entreprise ou SIRET..."
                      className="pl-9"
                      data-testid="company-search-input"
                    />
                    {searchingCompany && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                    )}
                  </div>
                  {companyResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {companyResults.map((company, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectCompany(company)}
                          className="w-full text-left p-2 hover:bg-white rounded border border-transparent hover:border-slate-200 transition-colors"
                        >
                          <p className="font-medium text-sm text-slate-900">{company.name}</p>
                          <p className="text-xs text-slate-500">
                            {company.siret} • {company.city}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_name">Nom *</Label>
              <div className="relative">
                <Input
                  id="client_name"
                  value={clientForm.name}
                  onChange={(e) => handleClientNameInput(e.target.value)}
                  onFocus={() => {
                    filterNameSuggestions(clientForm.name);
                    setShowNameSuggestions(true);
                  }}
                  onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
                  placeholder="Nom du client ou de l'entreprise"
                  autoComplete="off"
                  data-testid="client-name-input"
                />
                {showNameSuggestions && clientNameSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
                    {clientNameSuggestions.map((name, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectNameSuggestion(name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  data-testid="client-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={clientForm.address}
                onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                placeholder="123 Rue Example"
                data-testid="client-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={clientForm.postal_code}
                  onChange={(e) => setClientForm({ ...clientForm, postal_code: e.target.value })}
                  placeholder="75001"
                  data-testid="client-postal-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input
                  value={clientForm.city}
                  onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                  placeholder="Paris"
                  data-testid="client-city-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input
                  value={clientForm.siret}
                  onChange={(e) => setClientForm({ ...clientForm, siret: e.target.value })}
                  placeholder="123 456 789 00012"
                  data-testid="client-siret-input"
                />
              </div>
              <div className="space-y-2">
                <Label>N° TVA</Label>
                <Input
                  value={clientForm.tva_number}
                  onChange={(e) => setClientForm({ ...clientForm, tva_number: e.target.value })}
                  placeholder="FR12345678901"
                  data-testid="client-tva-input"
                />
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuler</Button>
              </DialogClose>
              <Button 
                type="submit"
                className="bg-[#065F46] hover:bg-[#047857] text-white"
                data-testid={editingClient ? 'update-client-btn' : 'create-client-btn'}
              >
                {editingClient ? 'Mettre à jour le client' : 'Créer le client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#065F46]" />
              Aperçu - {previewDocument?.number}
            </DialogTitle>
            <DialogDescription>
              Vérifiez votre {previewType === 'quote' ? 'devis' : 'facture'} avant de le télécharger
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto border border-slate-200 rounded-lg bg-white">
            {previewLoading ? (
              <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-[#065F46]" />
              </div>
            ) : (
              <iframe
                srcDoc={previewHtml}
                className="w-full h-[60vh] border-0"
                title="Aperçu du document"
              />
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">Fermer</Button>
            </DialogClose>
            <Button 
              onClick={() => {
                if (previewDocument) {
                  handleDownloadPdf(previewType, previewDocument.id, previewDocument.number);
                }
              }}
              className="bg-[#065F46] hover:bg-[#047857] text-white"
              data-testid="preview-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
